<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { FirebaseError } from 'firebase/app';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getFirebaseAuth } from '@/config/firebase';
import { getAuthErrorMessage, routeForBackendAuthState, useAuthStore } from '@/stores/auth';
import { isOfficeAddinHost, signInWithGoogleOfficeDialog } from '@/office/utils';
import { logger } from '@/utils/logger';
import GaiaAuthCard from '@/components/design/domain/auth/GaiaAuthCard.vue';
import GaiaAuthStatus from '@/components/design/domain/auth/GaiaAuthStatus.vue';
import GaiaGoogleButton from '@/components/design/domain/auth/GaiaGoogleButton.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDivider from '@/components/design/base/GaiaDivider.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaPasswordInput from '@/components/design/base/inputs/GaiaPasswordInput.vue';

/**
 * Sign-in for the Word task pane. The same card as the web login (Login.vue)
 * with the one difference the host forces: Google sign-in cannot redirect a
 * task pane, so it runs through an Office dialog window instead.
 */

const authStore = useAuthStore();
const router = useRouter();
const { error, loading } = storeToRefs(authStore);

const email = ref<string>('');
const password = ref<string>('');
const googleLoading = ref<boolean>(false);

// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
const maintenanceMessage = computed(() => import.meta.env.VITE_MAINTENANCE_MESSAGE || '');
const maintenanceDismissed = ref<boolean>(false);

const isAuthenticated = computed(() => authStore.isAuthenticated);
const isRedirectingToApp = ref<boolean>(false);
const currentUserEmail = computed(
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
  () => authStore.backendUser?.email || authStore.user?.email || ''
);

// Forgot password state
const showForgotPasswordModal = ref<boolean>(false);
const resetEmail = ref<string>('');
const resetPasswordLoading = ref<boolean>(false);
const resetPasswordError = ref<string | null>(null);
const resetPasswordSuccess = ref<boolean>(false);

function openFrontPage(): void {
  if (isRedirectingToApp.value) return;
  isRedirectingToApp.value = true;
  void router.push(isOfficeAddinHost() ? '/vault' : '/');
}

watch(
  () => authStore.isReady && authStore.isAuthenticated,
  isSignedIn => {
    if (isSignedIn) {
      openFrontPage();
    } else {
      isRedirectingToApp.value = false;
    }
  },
  { immediate: true }
);

// The isAuthenticated watch above never fires for partial-auth states
// (unverified / unprovisioned) — route those explicitly after sign-in.
function routePartialAuthState(): void {
  const partialRoute = routeForBackendAuthState(authStore.backendAuthState, '');
  if (partialRoute) {
    void router.push(partialRoute);
  }
}

async function handleSubmit(): Promise<void> {
  try {
    await authStore.signIn(email.value, password.value);
    routePartialAuthState();
  } catch (error_) {
    logger.error('Office login error:', error_);
  }
}

async function handleGoogleLogin(): Promise<void> {
  googleLoading.value = true;
  error.value = null;
  try {
    const payload = await signInWithGoogleOfficeDialog();
    await authStore.signInWithGoogleOfficePayload(payload);
    routePartialAuthState();
  } catch (error_) {
    logger.error('Office Google login error:', error_);
    // Read via the store: the sign-in actions set `error` internally on failure,
    // which TS's narrowing of `error.value` (nulled above) cannot see.
    error.value =
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      authStore.error ||
      (error_ instanceof Error ? error_.message : 'Google sign-in failed. Please try again.');
  } finally {
    googleLoading.value = false;
  }
}

function closeResetModal(): void {
  showForgotPasswordModal.value = false;
}

/** The reset modal opens pre-filled with whatever is already in the email field. */
function openResetModal(): void {
  resetEmail.value = email.value;
  resetPasswordError.value = null;
  resetPasswordSuccess.value = false;
  showForgotPasswordModal.value = true;
}

async function handleResetPassword(): Promise<void> {
  if (!resetEmail.value) {
    resetPasswordError.value = 'Please enter your email address';
    return;
  }

  resetPasswordLoading.value = true;
  resetPasswordError.value = null;

  try {
    await sendPasswordResetEmail(getFirebaseAuth(), resetEmail.value);
    // The dialog switches to its confirmation state and waits for "Done".
    resetPasswordSuccess.value = true;
  } catch (error_: unknown) {
    if (error_ instanceof FirebaseError) {
      resetPasswordError.value = getAuthErrorMessage(error_.code);
      logger.error('Password reset error:', error_.code, error_.message);
    } else {
      resetPasswordError.value = 'An unexpected error occurred';
      logger.error('Password reset error:', error_);
    }
  } finally {
    resetPasswordLoading.value = false;
  }
}
</script>

<template>
  <!-- The shell (canvas, mark above the card) is OfficeAuthLayout's; this page
       is only the card, as on the web. -->
  <gaia-auth-card
    v-if="!isAuthenticated"
    title="Welcome back"
    subtitle="Sign in to use GAIA in Word."
    width="100%"
  >
    <n-flex vertical :size="16">
      <gaia-banner
        v-if="maintenanceMessage && !maintenanceDismissed"
        plain
        type="warning"
        closable
        @close="maintenanceDismissed = true"
      >
        {{ maintenanceMessage }}
      </gaia-banner>
      <gaia-banner v-if="error" plain type="error">{{ error }}</gaia-banner>

      <gaia-form-field label="Email" variant="uppercase">
        <gaia-input
          :value="email"
          size="large"
          placeholder="you@firm.com"
          :disabled="loading"
          :input-props="{ type: 'email', autocomplete: 'email' }"
          style="width: 100%"
          @update:value="(v: string) => (email = v.toLowerCase())"
          @keydown.enter="handleSubmit"
        />
      </gaia-form-field>

      <gaia-form-field label="Password" variant="uppercase">
        <template #action>
          <gaia-link @click="openResetModal">Forgot password?</gaia-link>
        </template>
        <gaia-password-input
          v-model:value="password"
          size="large"
          placeholder="••••••••"
          :disabled="loading"
          :input-props="{ autocomplete: 'current-password' }"
          style="width: 100%"
          @keydown.enter="handleSubmit"
        />
      </gaia-form-field>
    </n-flex>

    <gaia-button
      type="primary"
      size="large"
      block
      :loading="loading"
      :style="{ marginTop: '22px' }"
      @click="handleSubmit"
    >
      Log in
    </gaia-button>

    <gaia-divider variant="subtle">or</gaia-divider>
    <!-- Opens the Office dialog window; the pane itself never leaves. -->
    <gaia-google-button :disabled="loading" :loading="googleLoading" @click="handleGoogleLogin">
      {{ googleLoading ? 'Waiting for Google…' : 'Continue with Google' }}
    </gaia-google-button>

    <template #footer>
      By logging in you agree to our
      <gaia-link href="https://www.gaia.law/tos" target="_blank">terms and conditions</gaia-link>
      and
      <gaia-link href="https://www.gaia.law/privacy" target="_blank">privacy policy</gaia-link>.
    </template>
  </gaia-auth-card>

  <!-- Signed in: the pane forwards to the vault on its own; this is the moment
       between, and a way back out. -->
  <gaia-auth-card v-else width="100%">
    <gaia-auth-status compact tone="success" title="You're signed in">
      <template #icon>
        <svg
          width="18"
          height="18"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M4 10.5 8.2 14.5 16 6" />
        </svg>
      </template>
      <template v-if="currentUserEmail">
        Signed in as <strong>{{ currentUserEmail }}</strong
        >. Opening GAIA…
      </template>
      <template v-else>Opening GAIA…</template>
      <template #actions>
        <gaia-button type="primary" size="large" block @click="openFrontPage"
          >Open GAIA</gaia-button
        >
        <gaia-button size="large" block @click="authStore.logout">Sign out</gaia-button>
      </template>
    </gaia-auth-status>
  </gaia-auth-card>

  <!-- Forgot password — the same two-state dialog as the web login. -->
  <gaia-modal
    v-model:show="showForgotPasswordModal"
    width="min(380px, calc(100vw - 24px))"
    content-style="padding: 26px 26px 22px"
    :closable="false"
  >
    <gaia-auth-status v-if="resetPasswordSuccess" compact tone="success" title="Check your inbox">
      <template #icon>
        <svg
          width="18"
          height="18"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M4 10.5 8.2 14.5 16 6" />
        </svg>
      </template>
      We sent a reset link to <strong>{{ resetEmail }}</strong
      >.
      <template #actions>
        <gaia-button type="primary" size="large" block @click="closeResetModal">Done</gaia-button>
      </template>
    </gaia-auth-status>

    <template v-else>
      <gaia-header :level="2" variant="display" size="dialog" :style="{ margin: '0 0 6px' }">
        Reset password
      </gaia-header>
      <gaia-text
        size="body"
        tone="tertiary"
        :style="{ display: 'block', marginBottom: '18px', lineHeight: 1.6 }"
      >
        Enter your email and we'll send you a reset link.
      </gaia-text>

      <gaia-form-field label="Email" variant="uppercase">
        <gaia-input
          v-model:value="resetEmail"
          size="large"
          placeholder="you@firm.com"
          :input-props="{ type: 'email', autocomplete: 'email' }"
          style="width: 100%"
          @keydown.enter="handleResetPassword"
        />
      </gaia-form-field>

      <gaia-banner v-if="resetPasswordError" plain type="error" :style="{ marginTop: '14px' }">
        {{ resetPasswordError }}
      </gaia-banner>

      <n-flex :size="10" :wrap="false" :style="{ marginTop: '20px' }">
        <gaia-button size="large" :style="{ flex: 1 }" @click="closeResetModal">Cancel</gaia-button>
        <gaia-button
          type="primary"
          size="large"
          :style="{ flex: 1 }"
          :loading="resetPasswordLoading"
          @click="handleResetPassword"
        >
          Send reset link
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
